import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import {
  UserCircleIcon,
  LockIcon,
  LogoutIcon,
  CpuIcon,
  InboxIcon,
  SunIcon,
} from '../components/ui/Icons';
import { useUnreadReleaseCount } from '../pages/WhatsNew/unreadStore';
import { useDeveloperMode } from './DeveloperModeContext';
import sidebarStyles from './Sidebar.module.css';
import styles from './UserMenu.module.css';
import { besideOrAbove, PopoverPosition } from './popoverPosition';

interface UserMenuProps {
  collapsed: boolean;
}

const initials = (name?: string) =>
  (name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

const UserMenu: React.FC<UserMenuProps> = ({ collapsed }) => {
  const { user, logout } = useAuth();
  const devMode = useDeveloperMode();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<PopoverPosition>({ left: 0, bottom: 0 });
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  /**
   * Novedades sin leer, para el globo.
   *
   * ## 🔑 Se pide al montar, sin intervalo
   *
   * Este menú vive en el sidebar, o sea en **todas** las pantallas. Un intervalo aquí serían peticiones constantes por
   * un número que cambia cuando se despliega algo — o sea, casi nunca.
   *
   * 🔄 **20-oct-2026: el número es el mismo del botón *What's new* del dashboard** (`unreadStore`). Antes este menú
   * pedía el suyo una sola vez y, como no se vuelve a montar al navegar, el globo seguía encendido después de leer las
   * novedades, hasta recargar la página. Ahora *What's new* lo apaga al marcarlas como leídas, aquí y en el dashboard.
   *
   * ⚠️ **Y nunca lanza.** Es un adorno informativo: si el endpoint falla —porque su SQL no ha corrido, o por red— el
   * menú tiene que seguir funcionando. Un fallo al contar novedades no puede tumbar el acceso a *cerrar sesión*; el
   * `catch` vive ahora en `unreadStore`.
   *
   * 📌 Se usa el endpoint que solo devuelve el número y no la lista completa: pedir todas las novedades en cada
   * pantalla para mostrar un globo sería trabajo por nada.
   */
  const unreadNews = useUnreadReleaseCount();

  const computePos = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    /** 🔄 21-oct-2026: a la derecha del botón si cabe; si no (el cajón del teléfono), encima. Ver `popoverPosition.ts`. */
    setPos(besideOrAbove(r, menuRef.current?.offsetWidth || 244));
  }, []);

  React.useLayoutEffect(() => {
    if (open) computePos();
  }, [open, computePos]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onScrollResize = () => computePos();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open, computePos]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    setOpen(false);
    navigate('/account');
  };

  /**
   * 📌 La bandeja también se abre desde aquí, además de la campanita.
   *
   * 🔑 Es lo personal junto a lo personal: "mis avisos" pertenece al mismo grupo que "mi perfil", no al menú
   * principal —que está organizado por dominio del negocio: embarques, usuarios, clientes—. Un renglón de
   * notificaciones entre los módulos compartidos sugeriría que es un módulo, y no lo es: es de cada quien.
   *
   * ⚠️ Y hace falta a pesar de la campanita: **la campanita se esconde si el endpoint no responde**, así que
   * sin este camino no habría forma de llegar a la bandeja el día que la lista falle — que es justo el día en
   * que alguien la va a buscar.
   */
  /**
   * NOVEDADES.
   *
   * 🔑 Va en este menú y **no en el principal**, por el mismo criterio que la bandeja: lo personal junto a lo personal.
   * El menú principal está organizado por dominio del negocio —embarques, usuarios, clientes— y un renglón de
   * novedades ahí sugeriría que es un módulo. No lo es: es información sobre la plataforma, y el estado de leído es de
   * cada quien.
   *
   * 📌 Lleva globo cuando hay algo sin leer. Sin el globo la pantalla existiría y nadie volvería a abrirla — es
   * exactamente el problema que el propio checklist señaló: *"un listado que siempre se ve igual no se vuelve a
   * abrir"*.
   */
  const handleWhatsNew = () => {
    setOpen(false);
    navigate('/whats-new');
  };

  const handleInbox = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={[sidebarStyles.item, styles.trigger, open ? styles.triggerOpen : ''].join(' ')}
        onClick={() => setOpen((o) => !o)}
        title={collapsed ? user?.name || 'Account' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className={sidebarStyles.icon}>
          <UserCircleIcon size={20} />
        </span>
        <span className={sidebarStyles.label}>My account</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            style={{ left: pos.left, bottom: pos.bottom }}
          >
            <div className={styles.identity}>
              <span className={styles.avatar}>{initials(user?.name)}</span>
              <div className={styles.identityText}>
                <span className={styles.name}>{user?.name || 'User'}</span>
                <span className={styles.email}>{user?.email || ''}</span>
              </div>
            </div>

            <div className={styles.divider} />

            {/**
             * Interruptor de la vista de Developer.
             *
             * Solo aparece si el usuario tiene `DEV_ACCESS`: sin permiso no hay nada
             * que activar, y mostrar un interruptor apagado que no hace nada sería
             * peor que no mostrarlo.
             *
             * Va aquí y no en la sección misma por una razón práctica: si estuviera
             * dentro del bloque de Developer, apagarlo lo haría desaparecer y no
             * quedaría desde dónde volver a encenderlo.
             */}
            {devMode.available && (
              <>
                <button
                  type="button"
                  className={[styles.action, styles.devToggle].join(' ')}
                  role="menuitemcheckbox"
                  aria-checked={devMode.enabled}
                  onClick={() => devMode.toggle()}
                >
                  <CpuIcon size={17} />
                  <span className={styles.devLabel}>Developer view</span>
                  <span
                    className={[styles.switch, devMode.enabled ? styles.switchOn : ''].join(' ')}
                    aria-hidden="true"
                  >
                    <span className={styles.knob} />
                  </span>
                </button>
                <div className={styles.divider} />
              </>
            )}

            {/**
             * 📌 Decía "Change Password" y llevaba a una pantalla en construcción. Ahora lleva a "Mi
             * perfil", que además de la contraseña muestra las sesiones abiertas y —cuando existan— la
             * verificación de correo y las preferencias de aviso. La etiqueta cambia con el destino:
             * "Change Password" haría que nadie encuentre el resto.
             */}
            <button type="button" className={styles.action} role="menuitem" onClick={handleProfile}>
              <LockIcon size={17} />
              My profile
            </button>
            <button type="button" className={styles.action} role="menuitem" onClick={handleInbox}>
              <InboxIcon size={17} />
              Notifications
            </button>
            <button type="button" className={styles.action} role="menuitem" onClick={handleWhatsNew}>
              <SunIcon size={17} />
              What&rsquo;s new
              {/**
               * 🔑 El globo es lo que hace que la pantalla se vuelva a abrir. Sin él, un listado que siempre se ve igual
               * se visita una vez.
               *
               * ⚠️ Lleva el número **y** el texto para el lector de pantalla: un punto de color no dice cuántas, y a
               * quien no distingue el color no le dice nada.
               */}
              {unreadNews > 0 && (
                <span className={styles.badge} aria-label={`${unreadNews} unread updates`}>
                  {unreadNews > 9 ? '9+' : unreadNews}
                </span>
              )}
            </button>
            <button
              type="button"
              className={[styles.action, styles.danger].join(' ')}
              role="menuitem"
              onClick={handleLogout}
            >
              <LogoutIcon size={17} />
              Log-Out
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

export default UserMenu;
